import {useLoadingStore} from "@/stores/loading";
import {useFaqSearch} from "@/stores/faqSearch";
import {useEffect, useRef, useState} from "react";
import {ConditionList, Faq} from "@/types/faq";
import * as Common from "@/utils/common";
import {getFaqList, getFaqSearchConditionList} from "@/api/faq";

export default function useFaqSearchHooks() {
  const setLoading = useLoadingStore(state => state.setLoading)
  const {
    order,
    setOrder,
    pageSize,
    setPageSize,
    checkTypeIds,
    setCheckTypeIds,
    checkKindIds,
    setCheckKindIds,
    searchText,
    setSearchText,
    currentPage,
    setCurrentPage
  } = useFaqSearch()

  const [isShowSearchOptions, setIsShowSearchOptions] = useState(false)
  const [isShowKeywordList, setIsShowKeywordList] = useState(false)

  const [lastModified, setLastModified] = useState("")
  const [faqList, setFaqList] = useState<Faq[]>([])
  const [conditionList, setConditionList] = useState<ConditionList>()

  const [displayFaqList, setDisplayFaqList] = useState<Faq[]>([])
  const [currentFaqList, setCurrentFaqList] = useState<Faq[]>([])
  const [firstItemNum, setFirstItemNum] = useState<number>(0)
  const [lastItemNum, setLastItemNum] = useState<number>(0)

  const onClickSearchScrollRef = useRef<HTMLDivElement>(null);

  const onClickSearchOptions = () => setIsShowSearchOptions(!isShowSearchOptions);
  const onClickKeywordList = () => setIsShowKeywordList(!isShowKeywordList);

  const onClickSearch = () => {
    const filterFaqs = Common.filterFaqs(faqList, checkTypeIds, checkKindIds, searchText || "");
    setDisplayFaqList(filterFaqs);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, pageSize, filterFaqs);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentFaqList(currentItems);
    setCurrentPage(0);
    Common.smoothScroll(onClickSearchScrollRef, -70);
  }
  const onClickReset = () => {
    setCheckTypeIds([])
    setCheckKindIds([])
    if (searchText) {
      setSearchText("")
    }
  }

  //検索オプション＿情報区分
  const onClickCheckType = (id: number) => {
    const newIds = checkTypeIds.includes(id) ? checkTypeIds.filter(typeId => typeId !== id) : [...checkTypeIds, id]
    setCheckTypeIds(newIds);
  }
  //検索オプション＿情報種別
  const onClickCheckKind = (id: number) => {
    const newIds = checkKindIds.includes(id) ? checkKindIds.filter(kindId => kindId !== id) : [...checkKindIds, id]
    setCheckKindIds(newIds);
  }
  //検索＿キーワード追加
  const onClickKeyword = (word: string) => {
    const updateText = searchText.length > 0 ? searchText + "　" + word : word;
    setSearchText(updateText)
  }

  //ページネーション＿表示件数変更
  const onChangePageSize = (e: any) => {
    setPageSize(e.target.value)
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, e.target.value, displayFaqList)
    setFirstItemNum(firstItemNum)
    setLastItemNum(lastItemNum)
    setCurrentFaqList(currentItems)
    setCurrentPage(0)
  }
  //ページネーション＿ページ移動
  const onChangePage = (e: any) => {
    setCurrentPage(e.selected)
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(e.selected, pageSize, displayFaqList)
    setFirstItemNum(firstItemNum)
    setLastItemNum(lastItemNum)
    setCurrentFaqList(currentItems)
  }

  // ページネーション＿先頭ページへ
  const goToFirstPage = () => {
    setCurrentPage(0);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, pageSize, displayFaqList);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentFaqList(currentItems);
  };

// ページネーション＿最後ページへ
  const goToLastPage = () => {
    const lastPage = Math.ceil(displayFaqList.length / Number(pageSize)) - 1;
    setCurrentPage(lastPage);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(lastPage, pageSize, displayFaqList);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentFaqList(currentItems);
  };

  //検索オプション・キーワード取得
  useEffect(() => {
    getFaqSearchConditionList().then(result => {
      setConditionList(result.data)
    })
    if (checkTypeIds.length > 0 || checkKindIds.length > 0) {
      setIsShowSearchOptions(true)
    }
  }, [])

  //FAQリストの取得
  useEffect(() => {
    setLoading(true)
    getFaqList(order).then(result => {
      const faqData = Common.addDisplayNum(result.data.faq_list)
      setFaqList(faqData)
      setLastModified(result.data.last_modified)

      const filterFaqs = Common.filterFaqs(faqData, checkTypeIds, checkKindIds, searchText)
      setDisplayFaqList(filterFaqs)
      //ページネーション
      const {
        firstItemNum,
        lastItemNum,
        currentItems
      } = Common.paginateCurrentValues(currentPage, pageSize, filterFaqs)
      setFirstItemNum(firstItemNum)
      setLastItemNum(lastItemNum)
      setCurrentFaqList(currentItems)
    }).catch(error => {
    }).finally(() => {
      setLoading(false)
    })
  }, [order])

  return {
    lastModified,
    onClickSearch,
    isShowSearchOptions,
    onClickSearchOptions,
    isShowKeywordList,
    onClickKeywordList,
    conditionList,
    onClickCheckType,
    onClickCheckKind,
    checkTypeIds,
    checkKindIds,
    onClickKeyword,
    onClickReset,
    displayFaqList,
    firstItemNum,
    lastItemNum,
    pageSize,
    onChangePageSize,
    order,
    setOrder,
    goToFirstPage,
    onChangePage,
    currentPage,
    goToLastPage,
    currentFaqList,
    onClickSearchScrollRef
  };
}